/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------- Design imports start -------------------------- */
import { Add } from '@mui/icons-material'
import {
  TextField,
  type TextFieldProps,
  Autocomplete,
  type AutocompleteRenderOptionState,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type HTMLAttributes, type ReactElement, useCallback, useEffect, useState } from "react";
/* ------------------------- Functional imports end ------------------------- */

const dedupeByValue = (
  opts: Array<{ value: string | number | boolean | null; label?: string; disabled?: boolean }> = []
) => {
  const seen = new Set<string | number | boolean | null | undefined>()
  return opts.filter(option => {
    const key = option?.value
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

type Props = {
  id?: string
  name: string
  value:
    | string
    | number
    | boolean
    | any[]
    | { value: string | number | boolean | null; label?: string; disabled?: boolean }
    | null
  errorMessage?: string
  options?: { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  loadData?: () => Promise<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >
  loadAllData?: () => Promise<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >
  disabled?: boolean
  required?: boolean
  onChange: (
    value:
      | string
      | number
      | boolean
      | null
      | any[]
      | { value: string | number | boolean | null; label?: string; disabled?: boolean }
  ) => void
  validation?: boolean
  onAddOption?: () => void
  multiple?: boolean
  fullWidth?: boolean
  // Autocomplete-specific props
  useAutocomplete?: boolean
  autocompleteHook?: {
    suggestions: Array<{ id: string; name: string } | { value: string; label: string }>
    isLoading: boolean
    updateQuery: (query: string) => void
    query: string
  }
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function SearchSelectInput<T>(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    name,
    value,
    options,
    loadData,
    disabled,
    errorMessage,
    validation,
    onChange,
    required,
    onAddOption,
    multiple,
    fullWidth,
    loadAllData,
    useAutocomplete,
    autocompleteHook,
  } = props
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [error, setError] = useState<boolean>(!value && required ? true : false)
  const [loading, setLoading] = useState<boolean>(false)
  const [newOptions, setNewOptions] = useState<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >(dedupeByValue(options || []))
  /* ----------------------------- Flag states end ---------------------------- */

  // Keep options deduped whenever prop changes
  // Skip if using autocomplete - the autocomplete effect handles options
  useEffect(() => {
    if (useAutocomplete) return
    setNewOptions(dedupeByValue(options || []))
  }, [options, useAutocomplete])

  useEffect(() => {
    if (!value && required) {
      setError(true)
    }
    if (validation !== false && !value) {
      setError(true)
    }
  }, [value])

  // Convert autocomplete suggestions to options format
  useEffect(() => {
    if (useAutocomplete && autocompleteHook) {
      const convertedOptions = autocompleteHook.suggestions.map(suggestion => {
        const s: any = suggestion
        // Prefer self URL for value (backend expects hyperlinks for relations)
        const value = s.self ?? s.id
        
        // Handle user suggestions: { id, full_name, email, self }
        if ('full_name' in suggestion) {
          const label = s.full_name || s.email || String(s.id)
          return { value, label }
        }
        // Handle suggestions with name field: { id, name, self }
        if ('name' in suggestion) {
          return { value, label: s.name }
        }
        // Handle {value, label} format (fallback)
        return suggestion as { value: string; label: string }
      })
      setNewOptions(dedupeByValue(convertedOptions))
    }
  }, [useAutocomplete, autocompleteHook?.suggestions])

  const handleOpen = useCallback(async () => {
    // If using autocomplete, trigger initial load of popular items
    if (useAutocomplete && autocompleteHook) {
      // Trigger query with empty string to load popular items
      if (autocompleteHook.query === '') {
        autocompleteHook.updateQuery('')
      }
    }
    // Legacy: Load data if not using autocomplete
    else if (!useAutocomplete && loadData) {
      setLoading(true)
      const data = await loadData()
      setNewOptions(dedupeByValue(data))
      if (loadAllData) {
        const fulldata = await loadAllData()
        setNewOptions([])
        setNewOptions(dedupeByValue(fulldata))
      }
      setLoading(false)
    }
  }, [loadData, loadAllData, useAutocomplete, autocompleteHook])

  const handleInputChange = useCallback((
    _event: React.SyntheticEvent, 
    newInputValue: string, 
    reason: string
  ) => {
    // Update autocomplete hook if enabled and user is typing
    if (useAutocomplete && autocompleteHook && reason === 'input') {
      autocompleteHook.updateQuery(newInputValue)
    }
  }, [useAutocomplete, autocompleteHook])

  /* ------------------------- Render constants start ------------------------- */
  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: any,
    state: AutocompleteRenderOptionState
  ) => {
    if (loading && option.value === 'add') {
      return (
        <ListItem
          key={option.value}
          style={{ display: 'flex', alignItems: 'center', zIndex: 9999 }}
          disablePadding
        >
          <ListItemButton dense>
            <ListItemIcon>
              <CircularProgress color="inherit" size={20} />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      )
    }
    if (!onAddOption && option.value === 'add') {
      return null
    }
    if (option.value === 'add') {
      return (
        <ListItem
          key={option.value}
          style={{ display: 'flex', alignItems: 'center' }}
          disablePadding
        >
          <ListItemButton id="addOption" onClick={onAddOption} dense>
            <ListItemIcon>
              <Add />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      )
    }
    return (
      <ListItem {...props} style={{ display: 'flex', alignItems: 'center' }}>
        <ListItemText primary={option.label} />
      </ListItem>
    )
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  if (
    error &&
    (value || (typeof value === 'object' && value !== null && Object.keys(value).length > 0))
  ) {
    // set error to false if there is any input for this component
    // This makes sure that the input field does not show error when an input value
    // was set by state rather using the onChange callback
    setError(false)
  }
  /* ------------------------- Pre render actions end ------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Autocomplete
      sx={{ zIndex: 1499 }}
      id={id || name}
      openOnFocus
      onOpen={handleOpen}
      onInputChange={handleInputChange}
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={useAutocomplete ? (autocompleteHook?.isLoading ?? false) : loading}
      loadingText={<CircularProgress color="inherit" size={20} />}
      getOptionLabel={(option: any) => {
        if (typeof option === 'string' || typeof option === 'number' || typeof option === 'boolean') {
          return String(option)
        }
        if (option?.label) return option.label
        if (option?.value) return String(option.value)
        return ''
      }}
      options={[{ value: 'add', label: 'add' }, ...newOptions] as any[]}
      value={multiple ? (Array.isArray(value) ? value : []) : (value || null)}
      isOptionEqualToValue={(option: any, val: any) => {
        // Compare by value so selections survive re-renders and new options arrays
        const optionValue = option?.value ?? option
        const valueValue = val?.value ?? val
        return optionValue === valueValue
      }}
      filterOptions={(options: any[], state: any) => {
        // Skip filtering if using autocomplete (filtering is done server-side)
        if (useAutocomplete) {
          return options
        }
        if (state.inputValue !== '' && state.index !== -1) {
          return options.filter((option: any) => {
            const optionLabel: string | undefined = option.label?.toLowerCase() as string | undefined
            return optionLabel?.includes(state.inputValue.toLowerCase())
          })
        } else {
          return options
        }
      }}
      renderOption={renderOption}
      disablePortal /* List of suggestions will not be rendered on top of everything */
      onChange={(_: any, option: any, reason: string) => {
        onChange(option)
        option !== '' ? setError(false) : setError(true)
      }}
      renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => (
        <TextField
          id={id || name}
          required={required}
          {...params}
          error={error}
          helperText={error && errorMessage}
          label={name}
          variant="outlined"
        />
      )}
    />
  )
}
